import { Button } from "@mui/material";
import { useState, useId } from "react";
import { useTranslations } from "use-intl";

import { DrawerTopBarWithAction } from "@/components/Drawer/DrawerTopBar";
import { PriceRangeSlider } from "@/components/Sliders/PriceRangeSlider";
import { BoardBasisFilter } from "@/features/hotels/components/hotels-list/BoardBasisFilter";
import { HotelClassFilter } from "@/features/hotels/components/hotels-list/HotelClassFilter";
import HotelsListsSort from "@/features/hotels/components/hotels-list/HotelsListSort";
import type {
  HotelsSortValue,
  IHotelsFilters,
} from "@/features/hotels/types/types";
import { initialSortValue } from "@/features/hotels/utils/initialHotelsValues";
import { useToggleSelectAll } from "@/hooks/useToggleSelectAll";
import { BOARD_BASIS_OPTIONS, HOTEL_CLASSES } from "@/utils/constants";

type Props = {
  defaultFilters: IHotelsFilters;
  filters: IHotelsFilters;
  bookingNights: number;
  sortValue: HotelsSortValue;
  onFiltersChange: (
    newFilters: Partial<IHotelsFilters>,
    newSort: HotelsSortValue,
  ) => void;
  onClose: () => void;
};

export function HotelsFilters({
  defaultFilters,
  filters,
  bookingNights,
  sortValue,
  onFiltersChange,
  onClose,
}: Props) {
  const priceHeaderId = useId();
  const t = useTranslations();
  const [localFilters, setLocalFilters] = useState<IHotelsFilters>({
    ...filters,
  });
  const [sort, setSort] = useState<HotelsSortValue>(sortValue);

  const {
    manuallySelectedAll: manuallySelectedAllHotelClasses,
    setManuallySelectedAll: setManuallySelectedAllHotelClasses,
    areAllSelected: areAllHotelClassesSelected,
    toggle: toggleHotelClasses,
  } = useToggleSelectAll<number>({
    selected: localFilters.starRating,
    all: HOTEL_CLASSES,
  });

  const areHotelClassesInDefaultState =
    areAllHotelClassesSelected && !manuallySelectedAllHotelClasses;

  const {
    manuallySelectedAll: manuallySelectedAllBoardBasis,
    setManuallySelectedAll: setManuallySelectedAllBoardBasis,
    areAllSelected: areAllBoardBasisSelected,
    toggle: toggleBoardBasis,
  } = useToggleSelectAll<string>({
    selected: localFilters.boardBasis || [],
    all: BOARD_BASIS_OPTIONS,
  });

  const areBoardBasisInDefaultState =
    areAllBoardBasisSelected && !manuallySelectedAllBoardBasis;

  return (
    <>
      <DrawerTopBarWithAction
        onCloseClick={onClose}
        title={t("hotels.filters.filters")}
        actionElement={
          <Button
            onClick={(e) => {
              setLocalFilters(defaultFilters);
              setSort(initialSortValue);
              e.currentTarget.blur();
            }}
            variant="text"
            size="small"
          >
            {t("clearFilters")}
          </Button>
        }
      />

      <div className="-mx-4 my-4 flex flex-grow flex-col gap-8 overflow-y-auto px-4">
        <div>
          <h3 className="mb-4 text-xl font-bold text-black">
            {t("hotels.filters.sort")}
          </h3>
          <HotelsListsSort value={sort} onChange={setSort} />
        </div>

        <div>
          <h3 id={priceHeaderId} className="mb-4 text-xl font-bold text-black">
            {t("hotels.filters.priceRangePerNight")}
          </h3>
          <PriceRangeSlider
            min={defaultFilters.priceRange[0]}
            max={defaultFilters.priceRange[1]}
            aria-labelledby={priceHeaderId}
            value={[localFilters.priceRange[0], localFilters.priceRange[1]]}
            scale={(value) => value / bookingNights}
            descale={(value) => Math.round(value * bookingNights)}
            onChange={(newValue) => {
              setLocalFilters((prevFilters) => ({
                ...prevFilters,
                priceRange: newValue,
              }));
            }}
          />
        </div>

        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="shrink-0 text-xl font-bold text-black">
              {t("hotels.filters.boardBasis")}
            </h3>
            <Button
              onClick={(e) => {
                if (areBoardBasisInDefaultState) {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    boardBasis: BOARD_BASIS_OPTIONS,
                  }));
                  setManuallySelectedAllBoardBasis(true);
                } else {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    boardBasis: defaultFilters.boardBasis,
                  }));
                  setManuallySelectedAllBoardBasis(false);
                }

                // Remove focus from the button after click
                e.currentTarget.blur();
              }}
              variant="text"
              size="small"
            >
              {areBoardBasisInDefaultState ? t("selectAll") : t("clearFilter")}
            </Button>
          </div>

          <BoardBasisFilter
            selected={localFilters.boardBasis}
            userManuallySelectedAll={manuallySelectedAllBoardBasis}
            onChange={(_e, checked, option) => {
              setLocalFilters((prevFilter) => {
                const newClasses = toggleBoardBasis(
                  prevFilter.boardBasis || [],
                  option,
                  checked,
                ).sort((a, b) => a.localeCompare(b));
                return { ...prevFilter, boardBasis: newClasses };
              });
            }}
          />
        </div>

        <div>
          <div className="mb-4 flex items-center justify-between">
            <h3 className="shrink-0 text-xl font-bold text-black">
              {t("hotels.filters.hotelClass")}
            </h3>
            <Button
              onClick={(e) => {
                if (areHotelClassesInDefaultState) {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    starRating: HOTEL_CLASSES,
                  }));
                  setManuallySelectedAllHotelClasses(true);
                } else {
                  setLocalFilters((prevFilters) => ({
                    ...prevFilters,
                    starRating: defaultFilters.starRating,
                  }));
                  setManuallySelectedAllHotelClasses(false);
                }

                // Remove focus from the button after click
                e.currentTarget.blur();
              }}
              variant="text"
              size="small"
            >
              {areHotelClassesInDefaultState
                ? t("selectAll")
                : t("clearFilter")}
            </Button>
          </div>

          <HotelClassFilter
            selectedClasses={localFilters.starRating}
            userManuallySelectedAllClasses={manuallySelectedAllHotelClasses}
            onChange={(_e, checked, option) => {
              setLocalFilters((prevFilter) => {
                const newClasses = toggleHotelClasses(
                  prevFilter.starRating,
                  option,
                  checked,
                ).sort((a, b) => a - b);
                return { ...prevFilter, starRating: newClasses };
              });
            }}
          />
        </div>
      </div>

      <div className="-mx-4 border-t border-t-stroke-neutral-soft px-4 pt-4">
        <Button
          fullWidth
          onClick={() => onFiltersChange(localFilters, sort)}
          variant="contained"
          size="large"
        >
          {t("apply")}
        </Button>
      </div>
    </>
  );
}
